/**
 * What situation is this household in — right now, and under the scenario being modelled?
 *
 * Three questions, each answered from nothing but the fact-find and the strategy options laid over
 * it: is this couple illness-separated, which partner should the aged-care modeller treat as being
 * in residential care, and do the MTA income bands read off the couple row or the single one. No
 * money is assessed here and nothing is indexed; these decide which RULE the assessment then applies.
 *
 * They live in their own module because of who asks them. The means test asks (financialCalculations
 * calls the first from inside `calculateFinancials`), but so does every aged-care and Support at Home
 * screen in the frontend — a dozen of them — none of which runs an assessment client-side. While
 * these three sat in financialCalculations.ts, that made the frontend a source-level consumer of the
 * whole assessment engine for the sake of three predicates over `adviceFor`, `relationship` and
 * `currentResidency`, and kept the engine in shared/ where it did not need to be.
 *
 * If you add a fourth, the test is the same: does it decide which rule applies (here) or compute a
 * figure under one (the assessors)?
 */

import type { FactFind } from './factFindTypes.ts';
import type { StrategyOptions } from './strategyOptionsTypes.ts';

/**
 * Illness-separated couple for **Age Pension** means tests (and any other caller that should mirror Centrelink "today").
 * `relationship.illnessSeparatedMode`: `auto` | `yes` | `no` (Dashboard, couples).
 */
export function isIllnessSeparatedCouple(factFind: FactFind, strategyOptions: StrategyOptions = {}): boolean {
    if (factFind?.adviceFor !== 'couple') return false;
    const mode = factFind?.relationship?.illnessSeparatedMode || 'auto';
    if (mode === 'yes') return true;
    if (mode === 'no') return false;

    const explicit = factFind?.relationship?.separatedByIllness;
    if (explicit === true) return true;
    if (explicit === false) return false;

    const clientRes = strategyOptions?.clientResidencyOverride || factFind?.client?.currentResidency;
    const partnerRes = strategyOptions?.partnerResidencyOverride || factFind?.partner?.currentResidency;
    const oneInRac = (clientRes === 'aged_care' && partnerRes !== 'aged_care') || (partnerRes === 'aged_care' && clientRes !== 'aged_care');
    if (oneInRac) return true;
    /*
     * BOTH in residential care (7 Oct 2026). Two residents are a couple living apart because of
     * illness — each in their own room, assessed on half the combined means — so each is paid at the
     * single rate. A couple sharing one room and able to live together is the exception, and the
     * profile's `illnessSeparatedMode: 'no'` says so.
     */
    if (clientRes === 'aged_care' && partnerRes === 'aged_care') return true;

    return false;
}

/**
 * For the **Aged Care modeller** only: treat one/both partners as in residential care for pension + MTA pathway logic
 * while Client Profile living situation may still show both at home.
 */
export function getAgedCareModellerResidencyOverrides(
    factFind: FactFind,
): { clientResidencyOverride?: string; partnerResidencyOverride?: string } {
    if (factFind?.adviceFor !== 'couple' || !factFind?.goals?.aged_care) return {};
    const forWho = factFind.goals.aged_care_for;
    const clientHome = factFind?.client?.currentResidency || 'own_home';
    const partnerHome = factFind?.partner?.currentResidency || 'own_home';
    if (forWho === 'client') {
        return { clientResidencyOverride: 'aged_care', partnerResidencyOverride: partnerHome };
    }
    if (forWho === 'partner') {
        return { clientResidencyOverride: clientHome, partnerResidencyOverride: 'aged_care' };
    }
    if (forWho === 'both') {
        return { clientResidencyOverride: 'aged_care', partnerResidencyOverride: 'aged_care' };
    }
    return { clientResidencyOverride: 'aged_care', partnerResidencyOverride: partnerHome };
}

/**
 * The residency the AGED CARE MODELLER lays over the profile — singles included.
 *
 * `getAgedCareModellerResidencyOverrides` above answers only for couples, because its other callers
 * (Support at Home's linked-care scenario) only need it there. The aged care module itself needs a
 * single entrant moved into care too: without it a single renter kept Rent Assistance in care, and a
 * single homeowner's former home never reached the two-year cut-off the projection applies — both
 * only ever happened for couples (owner's living-arrangement review, 7 Oct 2026). Aged care callers
 * read this; everything else keeps the couple-only one.
 */
export function agedCareEntrantResidencyOverrides(
    factFind: FactFind,
): { clientResidencyOverride?: string; partnerResidencyOverride?: string } {
    if (factFind?.adviceFor === 'couple') return getAgedCareModellerResidencyOverrides(factFind);
    if (!factFind?.goals?.aged_care) return {};
    return { clientResidencyOverride: 'aged_care' };
}

/**
 * Is a partner going to be LEFT LIVING IN THE FAMILY HOME once the other one enters residential care?
 *
 * A partner who stays put is a protected person, and a protected person in the home makes the home
 * exempt from the aged care means test (SA485 / CLIK 4.4.1). Nothing derived this before: the means
 * test read `home.protectedPerson` — a dropdown defaulting to `'no'` — and nothing ever set it from
 * the care goal. So the commonest couple in aged care (one in, one at home) had the home ASSESSED up
 * to the cap, inflating the means-tested fee and every RAD strategy built on it, while this very
 * module was already telling the rest of the engine the partner was at home.
 *
 * Residency comes from the same precedence the rest of this module uses — an explicit strategy
 * override first, then what the care goal implies, then what the fact-find recorded — so a modelled
 * scenario that moves someone is respected rather than second-guessed.
 *
 * Each person's OWN living arrangement decides it, never an assumption. A partner who is already
 * renting, in a retirement village or in a granny flat is not staying in a family home, so there is
 * nothing for their presence to exempt.
 */
export function partnerRemainsInFamilyHome(factFind: FactFind, strategyOptions: StrategyOptions = {}): boolean {
    if (factFind?.adviceFor !== 'couple') return false;

    /*
     * NO HOME-VALUE CONDITION, deliberately. This used to begin `if (home.value <= 0) return false`,
     * on the reasoning that a home worth nothing has no exemption to argue about. But whether a
     * partner is living in the home is a fact about the household, not about the valuation — and
     * the check was redundant twice over:
     *
     *   - the engine's only caller (agedCareCostsEngine, via hasAgedCareProtectedPerson) is already
     *     inside `if (home && Number(home.value) > 0 && ...)`, so it never asked without a value;
     *   - the client profile asks "who will stay in the home?" on the same screen that collects the
     *     value, so the question appeared while the field was blank and vanished as it was typed.
     *
     * Asked by the owner, 2026-08-20: "what does having a home value have to do with the fact that
     * we know the partner is living in the home?" Nothing, so it is gone.
     */
    const fromGoal = getAgedCareModellerResidencyOverrides(factFind);
    const clientRes = strategyOptions?.clientResidencyOverride || fromGoal.clientResidencyOverride || factFind?.client?.currentResidency || 'own_home';
    const partnerRes = strategyOptions?.partnerResidencyOverride || fromGoal.partnerResidencyOverride || factFind?.partner?.currentResidency || 'own_home';

    const clientInCare = clientRes === 'aged_care';
    const partnerInCare = partnerRes === 'aged_care';
    // Both in care leaves nobody behind; neither in care means the means test isn't running yet.
    if (clientInCare === partnerInCare) return false;
    return (clientInCare ? partnerRes : clientRes) === 'own_home';
}

/**
 * Whether residential aged care MTA income bands use the Schedule's **couple (illness-separated)** row
 * (`true`) or the **single** row (`false`) for the attributed half-income / half-asset inputs.
 */
export function resolveMtaCoupleIncomeThresholds(factFind: FactFind, strategyOptions: StrategyOptions = {}): boolean {
    if (factFind?.adviceFor !== 'couple') return false;
    const clientRes = strategyOptions?.clientResidencyOverride || factFind?.client?.currentResidency;
    const partnerRes = strategyOptions?.partnerResidencyOverride || factFind?.partner?.currentResidency;
    if (clientRes === 'aged_care' && partnerRes === 'aged_care') return true;
    if (factFind?.goals?.aged_care_for === 'both') return true;
    const oneInRac = (clientRes === 'aged_care' && partnerRes !== 'aged_care') || (partnerRes === 'aged_care' && clientRes !== 'aged_care');
    if (oneInRac) return true;
    if (factFind?.goals?.aged_care) {
        const agedFor = factFind.goals.aged_care_for;
        if (agedFor === 'client' || agedFor === 'partner') return true;
    }
    return isIllnessSeparatedCouple(factFind, strategyOptions);
}
